import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Edition, Product } from '../types';

import { ANALYTICS_CHANNEL, defaultAttributes, getAttributesFromContexts } from './use-analytics';

/**
 * This hook is designed to be used before contexts are initialized to avoid circular dependencies.
 */
function useOperationalFailureAnalytics({
	product,
	edition,
}: {
	product: Product;
	edition: Edition;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(attributes: Record<string, any>) => {
			const event = createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: '@atlassian/edition-awareness.edition-awareness',
				attributes: {
					...defaultAttributes,
					product,
					currentProductEdition: edition,
					...attributes,
				},
			});

			const contextAttributes = getAttributesFromContexts(event.context);

			event
				.update({
					attributes: {
						...contextAttributes,
						...event.payload.attributes,
					},
				})
				.fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, product, edition],
	);
}

/**
 * These are errors which we ignore from SLO reporting to ensure we reporting on errors we can action
 */
const nonSLOErrors = [
	/**
	 * These occur when the fetch request fails (usually this is because the users network is offline)
	 *
	 * This is usually thrown from inside of the products fetch handler
	 * e.g. jira/src/packages/platform/utils/fetch/src/utils/requests.tsx adds the handler which throws this error to every fetch request
	 */
	/failed to fetch/i,
	/**
	 * These occur when the fetch request fails due to a server response (e.g. 401 due to the user needing to reauthenticate their session)
	 * e.g. jira/src/packages/platform/utils/fetch/src/utils/requests.tsx adds the handler which throws server errors after parsing the response headers
	 */
	/Error server response:/i,
];

function isSLOError(error: Error) {
	// Just checks that none of our non slo errors match the error
	return !nonSLOErrors.some((msg) => msg.test(error.message));
}

/**
 * Use for tracking errors caught in error boundaries
 */
export function useErrorAnalytics({
	product,
	edition,
	id,
}: {
	product: Product;
	edition: Edition;
	id?: string;
}) {
	const sendOperationalFailure = useOperationalFailureAnalytics({ product, edition });

	return useCallback(
		(error: Error) => {
			sendOperationalFailure({
				error: error.message,
				errorStack: error.stack,
				errorName: error.name,
				id: id ?? 'unknownErrorId',
				isSLOError: isSLOError(error),
			});
		},
		[sendOperationalFailure, id],
	);
}

/**
 * Use when data is not completely broken (not caught by error boundary),
 * but is invalid or missing required data points.
 */
export function useDataValidationErrorAnalytics({
	product,
	edition,
}: {
	product: Product;
	edition: Edition;
}) {
	const sendOperationalFailure = useOperationalFailureAnalytics({ product, edition });

	return useCallback(
		(dataValidationError: Record<string, any>) => {
			sendOperationalFailure({
				dataValidationError,
				id: 'dataValidationError',
			});
		},
		[sendOperationalFailure],
	);
}
